/**
 * Custom JavaScript
 * =================
 * Consolidated theme JavaScript utilities and enhancements.
 *
 * Organization:
 * 1. CO Namespace & Utilities
 * 2. Event Emitter (pub/sub for CO components)
 * 3. Local Storage Helpers
 * 4. Initialization
 *
 * Best Practice: https://growth-services.shopify.com/blogs/blog/how-to-safely-customize-the-horizon-theme-to-maintain-upgradability
 *
 * Note: Component-specific scripts may use {% javascript %} tags in their
 * respective snippets. This file is for shared utilities only.
 */

(function() {
  'use strict';

  /* ==========================================================================
     1. CO Namespace & Utilities
     ========================================================================== */

  window.CO = window.CO || {};

  /**
   * Debounce function to limit rapid calls
   * @param {Function} func - Function to debounce
   * @param {number} wait - Milliseconds to wait
   * @returns {Function} Debounced function
   */
  CO.debounce = function(func, wait) {
    var timeout;
    return function executedFunction() {
      var context = this;
      var args = arguments;
      var later = function() {
        timeout = null;
        func.apply(context, args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  };

  /**
   * Throttle function to limit call frequency
   * @param {Function} func - Function to throttle
   * @param {number} limit - Minimum milliseconds between calls
   * @returns {Function} Throttled function
   */
  CO.throttle = function(func, limit) {
    var inThrottle;
    return function executedFunction() {
      var context = this;
      var args = arguments;
      if (!inThrottle) {
        func.apply(context, args);
        inThrottle = true;
        setTimeout(function() {
          inThrottle = false;
        }, limit);
      }
    };
  };

  /**
   * Format money using Shopify's money format
   * @param {number} cents - Amount in cents
   * @param {string} [format] - Money format string
   * @returns {string} Formatted money string
   */
  CO.formatMoney = function(cents, format) {
    if (typeof cents === 'string') {
      cents = cents.replace('.', '');
    }
    var value = '';
    var placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
    var formatString = format || window.Shopify?.money_format || '${{amount}}';

    function formatWithDelimiters(number, precision, thousands, decimal) {
      precision = precision === undefined ? 2 : precision;
      thousands = thousands || ',';
      decimal = decimal || '.';

      if (isNaN(number) || number === null) {
        return 0;
      }

      number = (number / 100.0).toFixed(precision);

      var parts = number.split('.');
      var dollarsAmount = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, '$1' + thousands);
      var centsAmount = parts[1] ? decimal + parts[1] : '';

      return dollarsAmount + centsAmount;
    }

    switch (formatString.match(placeholderRegex)?.[1]) {
      case 'amount':
        value = formatWithDelimiters(cents, 2);
        break;
      case 'amount_no_decimals':
        value = formatWithDelimiters(cents, 0);
        break;
      case 'amount_with_comma_separator':
        value = formatWithDelimiters(cents, 2, '.', ',');
        break;
      case 'amount_no_decimals_with_comma_separator':
        value = formatWithDelimiters(cents, 0, '.', ',');
        break;
      default:
        value = formatWithDelimiters(cents, 2);
    }

    return formatString.replace(placeholderRegex, value);
  };

  /* ==========================================================================
     2. Event Emitter (pub/sub for CO components)
     ========================================================================== */

  CO.events = {
    _handlers: {},

    /**
     * Subscribe to an event
     * @param {string} event - Event name
     * @param {Function} handler - Event handler
     * @returns {Function} Unsubscribe function
     */
    on: function(event, handler) {
      if (!this._handlers[event]) {
        this._handlers[event] = [];
      }
      this._handlers[event].push(handler);

      /* Return unsubscribe function */
      var handlers = this._handlers;
      return function() {
        var index = handlers[event].indexOf(handler);
        if (index > -1) {
          handlers[event].splice(index, 1);
        }
      };
    },

    /**
     * Emit an event
     * @param {string} event - Event name
     * @param {*} [data] - Event data
     */
    emit: function(event, data) {
      var handlers = this._handlers[event];
      if (handlers) {
        handlers.forEach(function(handler) {
          try {
            handler(data);
          } catch (e) {
            console.error('[CO] Event handler error:', e);
          }
        });
      }
    },

    /**
     * Subscribe to an event once
     * @param {string} event - Event name
     * @param {Function} handler - Event handler
     */
    once: function(event, handler) {
      var self = this;
      var unsubscribe = this.on(event, function(data) {
        unsubscribe();
        handler(data);
      });
    }
  };

  /* ==========================================================================
     3. Local Storage Helpers
     ========================================================================== */

  CO.storage = {
    prefix: 'co_',

    /**
     * Get item from localStorage
     * @param {string} key - Storage key
     * @param {*} [defaultValue] - Default value if not found
     * @returns {*} Stored value or default
     */
    get: function(key, defaultValue) {
      try {
        var item = localStorage.getItem(this.prefix + key);
        return item ? JSON.parse(item) : defaultValue;
      } catch (e) {
        console.error('[CO] Storage get error:', e);
        return defaultValue;
      }
    },

    /**
     * Set item in localStorage
     * @param {string} key - Storage key
     * @param {*} value - Value to store
     */
    set: function(key, value) {
      try {
        localStorage.setItem(this.prefix + key, JSON.stringify(value));
      } catch (e) {
        console.error('[CO] Storage set error:', e);
      }
    },

    /**
     * Remove item from localStorage
     * @param {string} key - Storage key
     */
    remove: function(key) {
      try {
        localStorage.removeItem(this.prefix + key);
      } catch (e) {
        console.error('[CO] Storage remove error:', e);
      }
    }
  };

  /* ==========================================================================
     4. Initialization
     ========================================================================== */

  /* ==========================================================================
     5. Logo Scroll Fade
     ========================================================================== */

  /**
   * Fades the full logo (text + icon) out over 0–50px scroll,
   * revealing the standalone icon underneath.
   * Matches shopper theme navbar scroll transition.
   */
  CO.logoScrollFade = function() {
    var FADE_END = 50;
    var ticking = false;
    var container = document.querySelector('.header-logo__image-container--original');
    var icon = document.querySelector('.co-logo-icon');
    if (!container || !icon) return;

    function update() {
      var scrollTop = window.scrollY;
      var opacity;
      if (scrollTop <= 0) {
        opacity = 1;
      } else if (scrollTop >= FADE_END) {
        opacity = 0;
      } else {
        opacity = 1 - (scrollTop / FADE_END);
      }
      container.style.opacity = opacity;
      icon.style.opacity = 1 - opacity;
      ticking = false;
    }

    function onScroll() {
      if (!ticking) {
        requestAnimationFrame(update);
        ticking = true;
      }
    }

    document.addEventListener('scroll', onScroll, { passive: true });
    update();
  };

  /* ==========================================================================
     6. Grid Selection (shopper parity)
     ========================================================================== */

  /**
   * Cleans up stale zoom-out values from sessionStorage
   * (zoom-out was replaced by co-3col/co-4col grid options).
   */
  CO.gridCleanup = function() {
    try {
      var desktopView = sessionStorage.getItem('product-grid-view-desktop');
      if (desktopView === 'zoom-out') {
        sessionStorage.removeItem('product-grid-view-desktop');
      }
    } catch(e) { /* ignore */ }
  };

  CO.init = function() {
    /* Emit ready event for components to hook into */
    CO.events.emit('co:ready');

    /* Initialize logo scroll fade */
    CO.logoScrollFade();

    /* Clean up stale grid values */
    CO.gridCleanup();

    /* Log init in development */
    if (window.Shopify?.designMode) {
      console.log('[CO] Custom scripts initialized');
    }
  };

  /* Initialize on DOM ready */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', CO.init);
  } else {
    CO.init();
  }

})();
